import { useEffect, useRef, useState } from 'react';
import { Button, Card, LoadingState } from '@beautyburst/ui';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import { customerHref } from '../base-path.ts';
import type {
  CustomerApiClient,
  CustomerCart,
  CustomerDeliveryQuote,
  CustomerDeliveryQuoteGroup,
} from '../api-client.ts';
import { CustomerApiError } from '../http-client.ts';
import { requestWithRetry } from '../request-retry.ts';

type Cart = CustomerCart;
type Address = Awaited<ReturnType<CustomerApiClient['addresses']['list']>>[number];
type OrderDetail = Awaited<ReturnType<CustomerApiClient['orders']['detail']>>;
type FulfillmentMode = 'PICKUP' | 'DELIVERY' | 'OVERNIGHT';

function actionKey(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `checkout-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : 'The checkout request could not be completed.';
}

/**
 * The API tags a CONFLICT failure with the real reason — e.g. `NOT_SERVICEABLE`
 * (an address outside the delivery area) versus `PRICE_CHANGED` (lines need a
 * refresh). The raw details envelope is untyped, so read it defensively.
 */
function conflictReason(details: unknown): string | null {
  if (typeof details !== 'object' || details === null) return null;
  const reason = (details as { reason?: unknown }).reason;
  if (Array.isArray(reason)) {
    const first = reason.find((entry): entry is string => typeof entry === 'string');
    return first ?? null;
  }
  if (typeof reason === 'string') return reason;
  return null;
}

function money(minor: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minor / 100);
}

function quoteReadyForPlacement(quote: CustomerDeliveryQuote, addressId?: string): boolean {
  return (
    quote.quoteId !== null &&
    !quote.requiresAddress &&
    !quote.requiresFulfillmentSelection &&
    (addressId === undefined || (addressId !== '' && quote.addressId === addressId))
  );
}

function quoteProblem(quote: CustomerDeliveryQuote, addressId?: string): string | null {
  if (quote.requiresFulfillmentSelection)
    return 'Choose a delivery option for every seller to receive a current price.';
  if (quote.requiresAddress) return 'Choose a delivery address to receive a current price.';
  if (addressId !== undefined && (addressId === '' || quote.addressId !== addressId))
    return 'Updating the price for the selected delivery address.';
  if (quote.quoteId === null)
    return 'A current price quote is unavailable. Refresh your cart before paying.';
  return null;
}

function MoneyLine({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div
      style={{
        alignItems: 'baseline',
        display: 'flex',
        fontWeight: emphasis ? 700 : 400,
        justifyContent: 'space-between',
        gap: space[4],
      }}
    >
      <dt>{label}</dt>
      <dd style={{ margin: 0 }}>{value}</dd>
    </div>
  );
}

function GroupMoneySummary({
  group,
  currency,
  sellerDisplayName,
}: {
  group: CustomerDeliveryQuoteGroup;
  currency: string;
  sellerDisplayName: string;
}) {
  return (
    <Card>
      <h4
        style={{
          ...typeStyle('body'),
          color: semanticColor.textPrimary,
          fontWeight: 700,
          margin: 0,
        }}
      >
        Sold by {sellerDisplayName}
      </h4>
      <dl style={{ display: 'grid', gap: space[2], margin: `${space[3]} 0 0` }}>
        <MoneyLine label="Items subtotal" value={money(group.subtotalMinor, currency)} />
        {group.fulfillmentMode === 'PICKUP' ? (
          <MoneyLine label="Fulfilment" value="Store pickup" />
        ) : group.freeDelivery ? (
          <MoneyLine label="Delivery" value="Free delivery" />
        ) : (
          <MoneyLine label="Delivery fee" value={money(group.deliveryFeeMinor, currency)} />
        )}
        {group.surchargeMinor > 0 ? (
          <MoneyLine label="Delivery surcharge" value={money(group.surchargeMinor, currency)} />
        ) : null}
        {group.benefitMinor > 0 && group.benefitLabel !== null ? (
          <MoneyLine label={group.benefitLabel} value={`−${money(group.benefitMinor, currency)}`} />
        ) : null}
      </dl>
    </Card>
  );
}

/** A server-quoted price breakdown. This component never derives an amount in the browser. */
export function MoneySummary({
  cart,
  heading = 'Price before you pay',
}: {
  cart: Cart;
  heading?: string;
}) {
  const { quote } = cart;
  const problem = quoteProblem(quote);
  const sellerNames = new Map(
    cart.groups.map((group) => [group.id, seller(group.ownershipType, group.sellerDisplayName)]),
  );
  return (
    <section aria-labelledby="money-summary-title" data-testid="section-money-summary">
      <h3
        id="money-summary-title"
        style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
      >
        {heading}
      </h3>
      {problem === null ? null : (
        <p
          role="alert"
          data-testid="status-current-price-unavailable"
          style={{ ...typeStyle('bodySm'), color: semanticColor.statusDangerText }}
        >
          {problem}
        </p>
      )}
      <div style={{ display: 'grid', gap: space[3] }}>
        {quote.groups.map((group) => (
          <GroupMoneySummary
            key={group.cartSellerGroupId}
            group={group}
            currency={quote.currency}
            sellerDisplayName={sellerNames.get(group.cartSellerGroupId) ?? group.sellerDisplayName}
          />
        ))}
      </div>
      <Card>
        <dl style={{ display: 'grid', gap: space[2], margin: 0 }}>
          <MoneyLine label="Subtotal" value={money(quote.subtotalMinor, quote.currency)} />
          <MoneyLine label="Tax" value={money(quote.taxMinor, quote.currency)} />
          <MoneyLine
            label="Total charged"
            value={money(quote.totalMinor, quote.currency)}
            emphasis
          />
        </dl>
      </Card>
    </section>
  );
}

function seller(ownershipType: string, sellerDisplayName: string): string {
  return ownershipType === 'BEAUTY_BURST_DIRECT' ? 'Beauty Burst' : sellerDisplayName;
}

function AddressForm({
  client,
  onCreated,
}: {
  client: Pick<CustomerApiClient, 'addresses'>;
  onCreated: (address: Address) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      aria-label="Add a delivery address"
      data-testid="form-add-delivery-address"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        setSaving(true);
        setError(null);
        void client.addresses
          .create({
            label: String(data.get('label') ?? ''),
            recipientName: String(data.get('recipientName') ?? ''),
            phone: String(data.get('phone') ?? ''),
            line1: String(data.get('line1') ?? ''),
            line2: String(data.get('line2') ?? ''),
            city: String(data.get('city') ?? ''),
            region: String(data.get('region') ?? ''),
            postalCode: String(data.get('postalCode') ?? ''),
            countryCode: String(data.get('countryCode') ?? ''),
            deliveryNotes: String(data.get('deliveryNotes') ?? ''),
          })
          .then(onCreated)
          .catch((nextError: unknown) => setError(messageFor(nextError)))
          .finally(() => setSaving(false));
      }}
      style={{ display: 'grid', gap: space[3], marginTop: space[4] }}
    >
      <h3 style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary, margin: 0 }}>
        Add an address
      </h3>
      <label>
        <span>Address label</span>
        <input
          data-testid="input-address-label"
          name="label"
          autoComplete="shipping address-level1"
        />
      </label>
      <label>
        <span>Recipient name</span>
        <input
          data-testid="input-recipient-name"
          name="recipientName"
          autoComplete="shipping name"
          required
        />
      </label>
      <label>
        <span>Phone</span>
        <input data-testid="input-address-phone" name="phone" autoComplete="tel" />
      </label>
      <label>
        <span>Address line 1</span>
        <input
          data-testid="input-address-line1"
          name="line1"
          autoComplete="shipping address-line1"
          required
        />
      </label>
      <label>
        <span>Address line 2</span>
        <input
          data-testid="input-address-line2"
          name="line2"
          autoComplete="shipping address-line2"
        />
      </label>
      <label>
        <span>City</span>
        <input
          data-testid="input-address-city"
          name="city"
          autoComplete="shipping address-level2"
          required
        />
      </label>
      <label>
        <span>State, province, or region</span>
        <input
          data-testid="input-address-region"
          name="region"
          autoComplete="shipping address-level1"
          required
        />
      </label>
      <label>
        <span>Postal code</span>
        <input
          data-testid="input-address-postal-code"
          name="postalCode"
          autoComplete="shipping postal-code"
          required
        />
      </label>
      <label>
        <span>Country code</span>
        <input
          data-testid="input-address-country-code"
          name="countryCode"
          autoComplete="shipping country"
          defaultValue="US"
          maxLength={2}
          required
        />
      </label>
      <label>
        <span>Delivery notes</span>
        <textarea data-testid="input-delivery-notes" name="deliveryNotes" />
      </label>
      {error === null ? null : (
        <p
          role="alert"
          data-testid="status-address-error"
          style={{ color: semanticColor.statusDangerText }}
        >
          {error}
        </p>
      )}
      <Button data-testid="button-save-delivery-address" type="submit" disabled={saving}>
        {saving ? 'Saving address' : 'Save address'}
      </Button>
    </form>
  );
}

function Confirmation({ order }: { order: OrderDetail }) {
  return (
    <section aria-labelledby="order-confirmation-title" data-testid="section-order-confirmation">
      <h2
        id="order-confirmation-title"
        style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
      >
        Order confirmed
      </h2>
      <p role="status">Your sandbox payment was captured.</p>
      <p>Order {order.id}</p>
      <p>Status: {order.status}</p>
      <p>Total: {money(order.total_minor, order.currency)}</p>
      <section aria-labelledby="confirmation-sellers-title">
        <h3
          id="confirmation-sellers-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Sellers
        </h3>
        <ul aria-label="Confirmed order sellers">
          {order.subOrders.map((subOrder) => (
            <li key={subOrder.id}>
              Sold by {seller(subOrder.ownership_type, subOrder.seller_display_name)} ·{' '}
              {subOrder.status}
            </li>
          ))}
        </ul>
      </section>
      <a data-testid="link-view-order" href={customerHref(`/orders/${order.id}`)}>
        View order and tracking
      </a>
    </section>
  );
}

export function CheckoutFlow({
  cart,
  client,
  onCartChanged,
}: {
  cart: Cart;
  client: Pick<CustomerApiClient, 'addresses' | 'analytics' | 'cart' | 'checkout' | 'orders'>;
  onCartChanged: (cart: Cart) => void;
}) {
  const [addresses, setAddresses] = useState<readonly Address[] | null>(null);
  const [addressId, setAddressId] = useState('');
  const [addingAddress, setAddingAddress] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<OrderDetail | null>(null);
  const [paymentTokenReference, setPaymentTokenReference] = useState('');
  const [provider, setProvider] = useState<'MOCK' | 'SANDBOX'>('MOCK');
  const [requestingTestToken, setRequestingTestToken] = useState(false);
  const [testTokenUnavailable, setTestTokenUnavailable] = useState(false);
  const [testTokenMessage, setTestTokenMessage] = useState<string | null>(null);
  const [requoteInProgress, setRequoteInProgress] = useState(false);
  const placementKey = useRef(actionKey());
  const captureKey = useRef(actionKey());

  useEffect(() => {
    let active = true;
    void requestWithRetry(() => client.addresses.list())
      .then((nextAddresses) => {
        if (!active) return;
        setAddresses(nextAddresses);
        const defaultAddress =
          nextAddresses.find((address) => address.id === cart.quote.addressId) ??
          nextAddresses.find((address) => address.is_default) ??
          nextAddresses[0];
        setAddressId(defaultAddress?.id ?? '');
      })
      .catch((nextError: unknown) => {
        if (active) {
          setAddresses([]);
          setError(messageFor(nextError));
        }
      });
    return () => {
      active = false;
    };
  }, [client]);

  const requoteForAddress = (nextAddressId: string) => {
    if (nextAddressId === '') return;
    setError(null);
    setRequoteInProgress(true);
    void requestWithRetry(() => client.cart.read(nextAddressId))
      .then(onCartChanged)
      .catch((nextError: unknown) => setError(messageFor(nextError)))
      .finally(() => setRequoteInProgress(false));
  };

  const selectFulfillment = (groupId: string, fulfillmentMode: FulfillmentMode) => {
    setError(null);
    void client.cart
      .changeFulfillmentMode(groupId, fulfillmentMode)
      .then((updated) => {
        const quote = updated.quote;
        onCartChanged({
          ...cart,
          currency: quote.currency,
          subtotalMinor: quote.subtotalMinor,
          taxMinor: quote.taxMinor,
          deliveryFeeMinor: quote.deliveryFeeMinor,
          deliverySurchargeMinor: quote.deliverySurchargeMinor,
          deliveryBenefitMinor: quote.deliveryBenefitMinor,
          totalMinor: quote.totalMinor,
          quote,
          groups: cart.groups.map((group) => {
            const quoteGroup = quote.groups.find(
              (candidate) => candidate.cartSellerGroupId === group.id,
            );
            return quoteGroup === undefined
              ? group
              : { ...group, fulfillmentMode: quoteGroup.fulfillmentMode };
          }),
        });
        if (addressId !== '' && quote.addressId !== addressId) requoteForAddress(addressId);
      })
      .catch((nextError: unknown) => setError(messageFor(nextError)));
  };

  const createAddress = (address: Address) => {
    setAddresses((current) => [...(current ?? []), address]);
    setAddressId(address.id);
    setAddingAddress(false);
    requoteForAddress(address.id);
  };

  const checkout = async (paymentTokenReference: string, provider: 'MOCK' | 'SANDBOX') => {
    const hasDeliveryChoice = cart.groups.every((group) => group.fulfillmentMode !== 'UNSELECTED');
    if (!hasDeliveryChoice) {
      setError('Choose a delivery option for every seller before placing your order.');
      return;
    }
    if (addressId === '') {
      setError('Choose or add a delivery address before placing your order.');
      return;
    }
    const deliveryQuoteId = cart.quote.quoteId;
    if (!quoteReadyForPlacement(cart.quote, addressId) || deliveryQuoteId === null) {
      setError(
        quoteProblem(cart.quote, addressId) ??
          'A current price quote is unavailable. Refresh your cart.',
      );
      return;
    }
    setError(null);
    setWorking(true);
    try {
      try {
        await client.analytics?.track({
          name: 'checkout.started',
          subjectKind: 'CART',
          subjectId: cart.cartId,
        });
      } catch {
        // Measurement is best effort and must never interrupt a customer purchase.
      }
      const placed = await client.checkout.place({
        cartId: cart.cartId,
        addressId,
        deliveryQuoteId,
        paymentTokenReference,
        provider,
        idempotencyKey: placementKey.current,
      });
      await client.checkout.capture({
        orderId: placed.id,
        amountMinor: placed.totalMinor,
        idempotencyKey: captureKey.current,
      });
      try {
        await client.analytics?.track({
          name: 'order.placed',
          subjectKind: 'CART',
          subjectId: placed.id,
        });
      } catch {
        // Measurement is best effort and must never interrupt a customer purchase.
      }
      const order = await requestWithRetry(() => client.orders.detail(placed.id));
      setConfirmation(order);
      try {
        onCartChanged(await requestWithRetry(() => client.cart.read()));
      } catch {
        // The confirmation is still truthful if refreshing a now-empty cart fails.
      }
    } catch (nextError) {
      if (nextError instanceof CustomerApiError && nextError.code === 'CONFLICT') {
        const reason = conflictReason(nextError.details);
        if (reason === 'NOT_SERVICEABLE') {
          // A real address problem, not a price change: refreshing and retrying
          // would loop forever, so tell the customer to pick a different address.
          setError(
            'This address is outside our current delivery area for one or more items in your cart. Choose a different delivery address to continue.',
          );
        } else {
          try {
            onCartChanged(await requestWithRetry(() => client.cart.read(addressId)));
            setError(
              'The price changed. Your cart has been refreshed. Review the updated total before placing your order.',
            );
          } catch {
            setError(
              'The price changed. Refresh your cart to review the updated total before placing your order.',
            );
          }
        }
      } else {
        setError(messageFor(nextError));
      }
    } finally {
      setWorking(false);
    }
  };

  const requestTestToken = async () => {
    setRequestingTestToken(true);
    setTestTokenMessage(null);
    try {
      const issued = await client.checkout.createPaymentToken({ provider });
      setPaymentTokenReference(issued.paymentTokenReference);
      // The API owns provider selection for the deployment. Use the provider it
      // actually issued rather than retaining an old browser default.
      setProvider(issued.provider);
      setTestTokenUnavailable(false);
      setTestTokenMessage('A test payment reference is ready for this test environment.');
    } catch {
      setTestTokenUnavailable(true);
      setTestTokenMessage(
        'Test payments are not enabled in this environment. Use a manually provided test reference instead.',
      );
    } finally {
      setRequestingTestToken(false);
    }
  };

  if (confirmation !== null) return <Confirmation order={confirmation} />;
  if (addresses === null) return <LoadingState label="Loading checkout" />;

  return (
    <section aria-labelledby="checkout-title" data-testid="section-checkout">
      <h2
        id="checkout-title"
        style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
      >
        Checkout
      </h2>
      <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
        Choose a delivery option for each seller, then use a sandbox payment reference to complete
        your order.
      </p>
      <MoneySummary cart={cart} />

      <section aria-labelledby="delivery-choice-title">
        <h3
          id="delivery-choice-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Delivery choices
        </h3>
        {cart.groups.map((group) => (
          <Card key={group.id}>
            <fieldset data-testid={`delivery-choice-${group.id}`}>
              <legend>Sold by {seller(group.ownershipType, group.sellerDisplayName)}</legend>
              {(['PICKUP', 'DELIVERY', 'OVERNIGHT'] as const).map((mode) => (
                <label
                  key={mode}
                  style={{
                    display: 'flex',
                    gap: space[2],
                    alignItems: 'center',
                    minHeight: '44px',
                  }}
                >
                  <input
                    data-testid={`radio-${group.id}-${mode.toLowerCase()}`}
                    type="radio"
                    name={`fulfillment-${group.id}`}
                    value={mode}
                    checked={group.fulfillmentMode === mode}
                    onChange={() => selectFulfillment(group.id, mode)}
                  />
                  {mode.charAt(0) + mode.slice(1).toLowerCase()}
                </label>
              ))}
            </fieldset>
          </Card>
        ))}
      </section>

      <section aria-labelledby="delivery-address-title" style={{ marginTop: space[6] }}>
        <h3
          id="delivery-address-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Delivery address
        </h3>
        <label>
          <span className="sr-only">Delivery address</span>
          <select
            aria-label="Delivery address"
            data-testid="select-delivery-address"
            value={addressId}
            onChange={(event) => {
              const nextAddressId = event.currentTarget.value;
              setAddressId(nextAddressId);
              requoteForAddress(nextAddressId);
            }}
          >
            <option value="">Select an address</option>
            {addresses.map((address) => (
              <option key={address.id} value={address.id}>
                {address.label ?? address.line1} — {address.city}, {address.region}
              </option>
            ))}
          </select>
        </label>
        {requoteInProgress ? (
          <p
            role="status"
            data-testid="status-refreshing-delivery-price"
            style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}
          >
            Updating the price for the selected delivery address.
          </p>
        ) : null}
        <Button
          data-testid="button-add-delivery-address"
          variant="secondary"
          onClick={() => setAddingAddress((visible) => !visible)}
          style={{ marginTop: space[3] }}
        >
          {addingAddress ? 'Cancel adding address' : 'Add a new address'}
        </Button>
        {addingAddress ? <AddressForm client={client} onCreated={createAddress} /> : null}
      </section>

      <section aria-labelledby="sandbox-payment-title" style={{ marginTop: space[6] }}>
        <h3
          id="sandbox-payment-title"
          style={{ ...typeStyle('headingSm'), color: semanticColor.textPrimary }}
        >
          Test-environment payment
        </h3>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const token = paymentTokenReference.trim();
            if (/^[\d\s-]{12,23}$/u.test(token)) {
              setError('Enter an opaque test reference, not payment credentials.');
              return;
            }
            void checkout(token, provider);
          }}
          style={{ display: 'grid', gap: space[3] }}
        >
          <p style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}>
            Test environment only. This form accepts an opaque test reference, never card details,
            CVV, or other payment credentials. The server selects the payment provider configured
            for this environment; customers cannot switch it from the browser.
          </p>
          {testTokenUnavailable ? null : (
            <Button
              data-testid="button-get-test-payment-reference"
              type="button"
              variant="secondary"
              disabled={requestingTestToken}
              onClick={() => void requestTestToken()}
            >
              {requestingTestToken ? 'Getting test reference' : 'Get a test payment reference'}
            </Button>
          )}
          {testTokenMessage === null ? null : (
            <p
              role="status"
              data-testid="status-test-payment-token"
              style={{ ...typeStyle('bodySm'), color: semanticColor.textSecondary }}
            >
              {testTokenMessage}
            </p>
          )}
          <label>
            <span>Manual sandbox or mock payment reference</span>
            <input
              data-testid="input-payment-token-reference"
              name="paymentTokenReference"
              value={paymentTokenReference}
              onChange={(event) => setPaymentTokenReference(event.currentTarget.value)}
              required
              minLength={1}
              maxLength={512}
              autoComplete="off"
            />
          </label>
          {error === null ? null : (
            <p
              role="alert"
              data-testid="status-checkout-error"
              style={{ color: semanticColor.statusDangerText }}
            >
              {error}
            </p>
          )}
          <Button
            data-testid="button-place-order"
            type="submit"
            disabled={
              working || requoteInProgress || !quoteReadyForPlacement(cart.quote, addressId)
            }
            style={{ minWidth: '44px' }}
          >
            {working ? 'Completing checkout' : 'Place order'}
          </Button>
        </form>
      </section>
    </section>
  );
}
